/**
 * @description Helper functions
 */

/**
 * Utility function to create a delay.
 *
 * @param timeout - The number of milliseconds to wait.
 * @returns A promise that resolves after the specified delay.
 */
export function wait(timeout = 0) {
  return new Promise((resolve) => setTimeout(resolve, timeout));
}

/**
 * Waits until an element matching the given selector is present in the DOM.
 *
 * @param selector - The CSS selector to match.
 * @returns A promise that resolves with the matching element.
 */
export function waitForElement(selector) {
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      if ((element = document.querySelector(selector))) {
        clearInterval(interval);
        resolve(element);
      }
    }, 100);
  });
}

/**
 * Converts a given amount into a formatted currency string based on the user's locale and currency settings.
 *
 * @param {number} amount - The amount of money to format. Defaults to 0.
 * @returns {string} A string representing the formatted currency amount.
 */
export function asMoney(amount = 0) {
  return new Intl.NumberFormat(`${Shopify.locale}-${Shopify.country}` || 'en-GB', {
    style: 'currency',
    currency: Shopify.currency.active || 'GBP',
  }).format(amount);
}

/**
 * Returns a new array with all duplicate elements removed. If a key is provided, uniqueness is determined by
 * the value of the key in each element. If no key is provided, uniqueness is determined by the stringified
 * representation of the elements.
 *
 * @param {array} array - The array to deduplicate.
 * @param {string} [key] - The key to use for determining uniqueness. Defaults to using the stringified representation of the elements.
 * @returns {array} A new array containing only unique elements.
 */
export function arrayUnique(array, key) {
  const seen = new Set();

  return array.filter(item => {
    const value = key ? item[key] : JSON.stringify(item);
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

/**
 * Calculates the percentage saving for a given variant based on its price and compare_at_price.
 *
 * @param {object} variant - The variant object containing price and compare_at_price properties.
 * @returns {number} The percentage saving, rounded to the nearest integer. Returns 0 if there is no saving.
 */
export function calculateVariantSavingPercentage(variant) {
  if (variant.compare_at_price > variant.price) {
    return Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
  }
  return 0;
}

/**
 * Calculates the absolute saving amount for a given variant based on its price and compare_at_price.
 *
 * @param {object} variant - The variant object containing price and compare_at_price properties.
 * @returns {number} The absolute saving amount. Returns 0 if there is no saving.
 */
export function calculateVariantSavingAmount(variant) {
  if (variant.compare_at_price > variant.price) {
    return variant.compare_at_price - variant.price;
  }
  return 0;
}